import { MutationCache, QueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast";

import { API_ERROR_MESSAGES, getErrorMessage, isAbortError, isApiError } from "./ApiError";

export const QUERY_STALE_TIME_MS = 30_000;
export const QUERY_MAX_RETRIES = 2;

export interface ApiMutationMeta extends Record<string, unknown> {
    /** Не показывать глобальный toast: мутация сама выводит ошибку (например, у формы). */
    silent?: boolean;
}

declare module "@tanstack/react-query" {
    interface Register {
        mutationMeta: ApiMutationMeta;
    }
}

/** Повторять сетевые ошибки и 5xx (до `QUERY_MAX_RETRIES` раз), но не 4xx и не прочие ошибки. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
    if (failureCount >= QUERY_MAX_RETRIES) return false;
    return isApiError(error) && (error.isNetwork || error.isServerError);
}

/** Текст глобального toast для ошибки мутации или `null`, если показывать нечего. */
export function getMutationErrorToast(error: unknown): string | null {
    if (isAbortError(error)) return null;
    if (isApiError(error)) {
        // 401 после неудачного refresh — сессия завершена, этим занимается onSessionEnded (выход на /login).
        if (error.isUnauthorized) return null;
        if (error.isForbidden) return API_ERROR_MESSAGES[403];
    }
    // 409 — текст сервера: бизнес-конфликт, пользователь должен его прочитать. 413/415 — тексты про файл.
    return getErrorMessage(error);
}

/**
 * QueryClient приложения. Один на вкладку (`src/app/providers.tsx`); в тестах — новый на каждый тест.
 * При завершении сессии кэш очищается (`queryClient.clear()` в провайдере).
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        mutationCache: new MutationCache({
            onError(error, _variables, _context, mutation) {
                if (mutation.meta?.silent) return;
                const message = getMutationErrorToast(error);
                if (message) toast.error(message);
            },
        }),
        defaultOptions: {
            queries: {
                staleTime: QUERY_STALE_TIME_MS,
                retry: shouldRetryQuery,
                refetchOnWindowFocus: true,
            },
            mutations: {
                retry: false,
            },
        },
    });
}
